import unionBy from 'lodash/unionBy';
import { IActTeamMemberAvailableList } from '~/services/api/actTeamMember/types';
import {
  IAddClientAllocation,
  IAllocationSelectedClient,
  IUpdateClientAllocation,
} from '~/services/api/clientAllocation';
import {
  IAllocationClientsByGroup,
  IClientAllocationDetailed,
  IClientAllocationPayload,
  IRelatedClientsForAllocation,
} from '~/services/api/clientAllocation/types';
import { IRole } from '~/store/user/types';
import { IOption } from '~/types';
import {
  getClientMemberFormFieldName,
  getClientsFormFieldName,
  getPayloadClients,
} from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/helpers/getters';

export interface ITeamMemberMapped {
  id: number;
  fullName: string;
  photo: string | null;
  isDeleted: boolean;
  roles?: IRole[];
}

interface IGroupedClientAllocationData {
  allocationsForEdit: IUpdateClientAllocation[];
  allocationsForAdd: IAddClientAllocation[];
  allocationsForDelete: number[];
}

export const formValuesMapper = (obj: { [key: string]: number[] }): number[] =>
  Object.keys(obj).reduce(
    (acc, val) =>
      val.startsWith('clients_') || val.startsWith('groups_') ? [...acc, ...(obj[val] || [])] : acc,
    [],
  );

export const groupClientAllocationData = (
  values: Record<string, unknown>,
  editAllocationIds?: string[],
  clientsAllocations?: IClientAllocationDetailed[],
): IGroupedClientAllocationData => {
  const result = Object.keys(values)
    .filter(key => !key.includes('groups') && !key.includes('memo'))
    .reduce(
      (acc: IGroupedClientAllocationData, key: string) => {
        const keyArray = key.split('_');
        const userId = keyArray[1];
        const allocationId = keyArray[2];

        const locationGroupIds = Array.from(
          new Set([
            ...((values[`groups_${userId}${allocationId ? `_${allocationId}` : ''}`] as number[]) ||
              []),
          ]),
        ); // remove duplicates

        if (key.startsWith('clients')) {
          const item = values[key] as number[];
          const itemLength = item?.length;
          if (allocationId && editAllocationIds?.includes(allocationId)) {
            if (itemLength === 0) {
              // delete allocation
              acc.allocationsForDelete.push(Number(allocationId));
              // edit allocation
            } else if (itemLength && allocationId) {
              // } else if (itemLength && !crisisAllocationsIds?.includes(Number(allocationId))) {
              const selectedClients = getPayloadClients(
                item,
                values,
                Number(userId),
                Number(allocationId),
              );
              const currentAllocation = clientsAllocations?.find(
                allocation => allocation.id === Number(allocationId) && !allocation.isCrisis,
              );
              // we need to check if current allocation is crisis,
              // we can edit only memo for crisis allocation (select field to choose new clients in this case is disabled),
              // if user has regular allocations for this date - we need to find regular allocation id and add clients
              if (currentAllocation) {
                acc.allocationsForEdit.push({
                  id: String(currentAllocation.id),
                  clients: selectedClients,
                  locationGroupIds,
                });
              }
            }
          } else if (itemLength && !allocationId) {
            // add allocation
            acc.allocationsForAdd.push({
              userId: Number(userId),
              clients: getPayloadClients(item, values, Number(userId), Number(allocationId)),
              locationGroupIds,
            });
          }
        }

        return acc;
      },
      { allocationsForEdit: [], allocationsForAdd: [], allocationsForDelete: [] },
    );

  // Also check allocations that were not in editAllocationIds but have clients removed or changed
  // Always prefer form values over updatedAllocations to ensure we have the latest client list
  // This ensures allocations with duplicate clients are handled independently
  if (clientsAllocations) {
    clientsAllocations.forEach(allocation => {
      if (allocation.isCrisis || allocation.isArchived) return;

      const allocationId = String(allocation.id);
      const userId = allocation.user.id;
      const formFieldKey = `clients_${userId}_${allocation.id}`;
      const formClients = (values[formFieldKey] as number[]) || [];

      // Skip if already processed in first pass
      const alreadyInEdit = result.allocationsForEdit.some(a => a.id === allocationId);
      const alreadyInDelete = result.allocationsForDelete.includes(allocation.id);
      if (alreadyInEdit || alreadyInDelete) return;

      // Get original client IDs from clientsAllocations for comparison
      const originalAllocation = clientsAllocations?.find(a => a.id === allocation.id);
      const originalClientIds = originalAllocation?.user.clients?.map(c => c.id) || [];

      // Always prefer form values - they contain the actual selected clients
      // Only fall back to updatedAllocations if form field doesn't exist
      const clientsToUse =
        formClients.length > 0 ? formClients : allocation.user.clients?.map(c => c.id) || [];

      // Check if clients were added or removed compared to original
      const sortedCurrentClients = [...clientsToUse].sort((a, b) => a - b);
      const sortedOriginalClients = [...originalClientIds].sort((a, b) => a - b);
      const hasChanged =
        sortedCurrentClients.length !== sortedOriginalClients.length ||
        JSON.stringify(sortedCurrentClients) !== JSON.stringify(sortedOriginalClients);

      if (hasChanged && clientsToUse.length > 0) {
        // Clients were modified but allocation still has clients - add to allocationsForEdit
        // This handles cases where the same clients are in multiple allocations independently
        const locationGroupIds = Array.from(
          new Set([...((values[`groups_${userId}_${allocation.id}`] as number[]) || [])]),
        );
        const selectedClients = getPayloadClients(clientsToUse, values, userId, allocation.id);
        result.allocationsForEdit.push({
          id: allocationId,
          clients: selectedClients,
          locationGroupIds,
        });
      } else if (hasChanged && clientsToUse.length === 0 && originalClientIds.length > 0) {
        // All clients were removed - mark for deletion
        result.allocationsForDelete.push(allocation.id);
      }
    });
  }

  return result;
};

export function mergeAllocationByUserId(
  clientAllocations: IClientAllocationDetailed[],
): IClientAllocationDetailed[] {
  const mergedArray: IClientAllocationDetailed[] = [];

  clientAllocations.forEach(item => {
    // Find an existing entry with the same user ID
    const existing = mergedArray.find(i => i.user.id === item.user.id && !i.isArchived);

    if (existing) {
      // Merge clients and locationGroups
      existing.user = {
        ...existing.user,
        clients: unionBy(
          [...(existing.user.clients || []), ...(item.user.clients || [])].map(client => ({
            ...client,
          })),
          'id',
        ),
      };

      // Merge locationGroups, ensuring immutability
      existing.locationGroups = unionBy(
        [...(existing.locationGroups || []), ...(item.locationGroups || [])].map(group => ({
          ...group,
        })),
        'id',
      );
    } else {
      // Add a deep copy of the item to avoid reference issues
      mergedArray.push({
        ...item,
        user: {
          ...item.user,
          clients: item.user.clients.map(client => ({ ...client })),
        },
        locationGroups: item.locationGroups.map(group => ({ ...group })),
      });
    }
  });

  return mergedArray;
}

export const prepareDefaultValues = (
  clientAllocations: IClientAllocationDetailed[],
): { [key: string]: IAllocationSelectedClient[] } => {
  const usr = mergeAllocationByUserId(clientAllocations).reduce((acc, currentValue) => {
    if (currentValue.isArchived || !currentValue.user?.clients?.length) {
      return acc;
    }

    const getClientsField = () => ({
      [`clients_${currentValue.user.id}_${currentValue.id}`]: currentValue.user.clients.map(
        item => item.id,
      ),
    });

    const getMemoField = () =>
      currentValue.user?.clients?.reduce(
        (prevClient, client) => ({
          ...prevClient,
          [getClientMemberFormFieldName(
            'memo_',
            client.id,
            getClientsFormFieldName(currentValue.user.id, currentValue.id),
          )]: client.memo,
        }),
        {} as Record<string, string>,
      );

    const getGroupsField = () => ({
      [`groups_${currentValue.user.id}_${currentValue.id}`]:
        currentValue.locationGroups?.map(item => item.id) || [],
    });

    return {
      ...acc,
      ...getClientsField(),
      ...getMemoField(),
      ...getGroupsField(),
    };
  }, {});

  return usr;
};

export const teamMemberMapper = (teamMember: IActTeamMemberAvailableList): ITeamMemberMapped => ({
  id: teamMember.id,
  fullName: `${teamMember.firstName} ${teamMember.lastName}`,
  photo: teamMember.photo,
  isDeleted: teamMember.isDeleted || !teamMember.isActive,
  roles: teamMember.roles,
});

export const clientMapper = (client: IRelatedClientsForAllocation): IOption => ({
  value: client.id,
  label: `${client.firstName} ${client.lastName}`,
  photo: client.photo,
  name: `${client.firstName} ${client.lastName}`,
  id: client.id,
  teams: [client.actTeam],
});

export const formatLocationGroupOptions = (groups: IAllocationClientsByGroup[]): IOption[] =>
  (groups || []).map(group => ({
    value: group.locationGroup.id,
    label: group.locationGroup.name,
  }));

export const sortByName = (
  a: IActTeamMemberAvailableList,
  b: IActTeamMemberAvailableList,
): number => (a.lastName.toLocaleLowerCase() < b.lastName.toLocaleLowerCase() ? -1 : 1);

// need to extract all assigned clients ids
export const clientAllocationMapper = (clientAllocations: IClientAllocationDetailed[]): number[] =>
  clientAllocations
    .map(item => item.user.clients)
    .flat()
    .map(item => item.id);

interface IFormValues {
  includeWeekends: boolean;
  allocationMode: string;
  clientAllocations?: IClientAllocationDetailed[];
  dateRange: [string, string];
  [key: string]: unknown;
}

export const transformFormToPayload = (values: IFormValues): IClientAllocationPayload => {
  const allocations = Object.entries(values).reduce((acc, [key, value]) => {
    if (key.startsWith('clients_')) {
      const memberId = Number(key.split('_')[1]);
      const allocationId = Number(key.split('_')[2]);
      if (value === undefined || !Array.isArray(value)) return acc;

      const activeClients = (value as number[]).filter((clientId: number) => {
        const clientAllocation = values.clientAllocations?.find(ca =>
          ca.user.clients.some(c => c.id === clientId),
        );
        return !clientAllocation?.isArchived;
      });

      if (activeClients.length === 0) return acc;

      const clients = activeClients.map(clientId => ({
        id: clientId,
        teamId:
          ((allocationId
            ? Number(values[`teamId_${clientId}_${memberId}_${allocationId}`]) || undefined
            : Number(values[`teamId_${clientId}_${memberId}`])) as number) || undefined,
        memo:
          ((allocationId
            ? values[`memo_${clientId}_${memberId}_${allocationId}`]
            : values[`memo_${clientId}_${memberId}`]) as string) || '',
      }));

      const locationGroupIds = Array.from(
        new Set([
          ...((values[`groups_${memberId}${allocationId ? `_${allocationId}` : ''}`] as number[]) ||
            []),
        ]),
      ); // remove duplicates

      if (allocationId) {
        acc.push({
          userId: memberId,
          id: allocationId,
          locationGroupIds,
          clients,
        });

        return acc;
      }

      acc.push({
        userId: memberId,
        id: allocationId,
        locationGroupIds,
        clients,
      });
    }
    return acc;
  }, [] as IClientAllocationPayload['allocations']);
  return {
    from: values.dateRange[0],
    to: values.dateRange[1],
    includeWeekends: values.includeWeekends,
    allocations: allocations.filter(a => a.clients.length > 0),
  };
};

/**
 * Adjusts form values to map clients to their correct allocation IDs based on crisis allocations.
 * This is needed when clients from different allocations are shown together but need to be
 * separated back into their individual allocations.
 * Returns adjusted form values with clients split by allocation ID.
 */
// eslint-disable-next-line import/prefer-default-export, import/no-unused-modules
export const adjustFormValuesByCrisisAllocations = (
  formValues: Record<string, unknown>,
  crisisAllocations: IClientAllocationDetailed[],
): Record<string, unknown> => {
  const adjustedValues: Record<string, unknown> = {};
  const groupsToProcess: Array<{ userId: string; value: unknown }> = [];

  // Create a deep copy of allocations to avoid mutating the original
  const updatedAllocations = crisisAllocations.map(allocation => ({
    ...allocation,
    user: {
      ...allocation.user,
      clients: allocation.user.clients?.map(client => ({ ...client })) || [],
    },
  }));

  // Create a map of clientId -> array of allocations (client can be in multiple allocations)
  const clientToAllocationMap = new Map<number, Array<{ userId: number; allocationId: number }>>();
  // Pre-build maps for faster lookups
  const allocationsByUserId = new Map<number, IClientAllocationDetailed[]>();
  const allocationsById = new Map<number, IClientAllocationDetailed>();
  const memoFieldsIndex = new Map<string, string>(); // key -> memoKey for faster lookup

  // Build indexes
  updatedAllocations.forEach(allocation => {
    const userId = allocation.user.id;
    const allocationId = allocation.id;

    // Index by userId
    if (!allocationsByUserId.has(userId)) {
      allocationsByUserId.set(userId, []);
    }
    allocationsByUserId.get(userId)?.push(allocation);

    // Index by allocationId
    allocationsById.set(allocation.id, allocation);

    // Build client-to-allocation map
    allocation.user.clients?.forEach(client => {
      if (!clientToAllocationMap.has(client.id)) {
        clientToAllocationMap.set(client.id, []);
      }
      clientToAllocationMap.get(client.id)?.push({
        userId,
        allocationId,
      });
    });
  });

  // Pre-index memo fields for faster lookup
  Object.keys(formValues).forEach(key => {
    if (key.startsWith('memo_')) {
      memoFieldsIndex.set(key, key);
    }
  });

  // First pass: split clients by allocation and handle memos
  Object.entries(formValues).forEach(([key, value]) => {
    if (key.startsWith('clients_')) {
      const clients = value as number[];
      const parts = key.split('_');
      const [, userId, allocationId] = parts;

      // If allocationId is missing (e.g., clients_246), copy as-is without processing
      if (!allocationId) {
        adjustedValues[key] = value;
        // Copy related memos for this user-level field (memo_CLIENTID_USERID format)
        clients?.forEach(clientId => {
          // Use pre-indexed memo fields for faster lookup
          const memoPrefix = `memo_${clientId}_${userId}`;
          const memoKey = Array.from(memoFieldsIndex.keys()).find(
            k => k.startsWith(memoPrefix) && k.split('_').length === 3, // memo_CLIENTID_USERID (no allocationId)
          );
          if (memoKey && formValues[memoKey]) {
            adjustedValues[memoKey] = formValues[memoKey];
          }
        });
        return;
      }

      const userIdNum = Number(userId);
      // Use cached user allocations lookup
      const userAllocations = allocationsByUserId.get(userIdNum) || [];

      // If clients array is empty, create empty entries for all allocations for this user
      if (clients?.length === 0) {
        userAllocations.forEach(alloc => {
          adjustedValues[`clients_${alloc.user.id}_${alloc.id}`] = [];
          adjustedValues[`groups_${alloc.user.id}_${alloc.id}`] = [];
        });
        return;
      }

      // Check for allocations in updatedAllocations for this user that should be empty
      // (i.e., allocations whose clients are not in the current form)
      const clientsSet = new Set(clients); // Use Set for O(1) lookup
      userAllocations.forEach(alloc => {
        // Check if any clients from this allocation are in the current clients array
        const hasClientsInForm = alloc.user.clients?.some(client => clientsSet.has(client.id));
        const clientsKey = `clients_${alloc.user.id}_${alloc.id}`;

        if (!hasClientsInForm && !adjustedValues[clientsKey]) {
          // This allocation has clients but they were removed - create empty entry
          adjustedValues[clientsKey] = [];
          adjustedValues[`groups_${alloc.user.id}_${alloc.id}`] = [];
        }
      });

      // Always split clients according to updatedAllocations structure
      // This ensures that clients are correctly distributed to their respective allocations
      // based on how they exist in updatedAllocations, not just form values
      const clientsByAllocation = new Map<string, number[]>();
      const memosByClientId = new Map<number, string>();
      const newlyAddedClients = new Map<string, number[]>(); // Track newly added clients per allocation

      clients.forEach(clientId => {
        const allocations = clientToAllocationMap.get(clientId) || [];
        const isNewlyAdded = allocations.length === 0;

        // Find which allocation(s) this client belongs to in updatedAllocations
        // If client is in multiple allocations for this user, we need to find the correct one
        // Priority: 1) exact match (userId + allocationId from form field), 2) any allocation for this user
        let allocationData = allocations.find(
          alloc => alloc.userId === Number(userId) && alloc.allocationId === Number(allocationId),
        );

        // If no exact match but client exists in other allocations for this user, use the first one
        if (!allocationData && allocations.length > 0) {
          allocationData = allocations.find(alloc => alloc.userId === Number(userId));
        }

        // If client is not in the map (newly added), use the allocation from the form field
        if (!allocationData) {
          allocationData = {
            userId: Number(userId),
            allocationId: Number(allocationId),
          };
        }

        const allocationKey = `${allocationData.userId}_${allocationData.allocationId}`;
        if (!clientsByAllocation.has(allocationKey)) {
          clientsByAllocation.set(allocationKey, []);
        }
        clientsByAllocation.get(allocationKey)?.push(clientId);

        // Track newly added clients
        if (isNewlyAdded) {
          if (!newlyAddedClients.has(allocationKey)) {
            newlyAddedClients.set(allocationKey, []);
          }
          newlyAddedClients.get(allocationKey)?.push(clientId);
        }

        // Find and store memo for this client
        // Try to find exact match first: memo_CLIENTID_USERID_ALLOCATIONID
        const exactMemoKey = `memo_${clientId}_${userId}_${allocationId}`;
        const memoKey = memoFieldsIndex.has(exactMemoKey)
          ? exactMemoKey
          : Array.from(memoFieldsIndex.keys()).find(k => k.startsWith(`memo_${clientId}_`));
        if (memoKey && formValues[memoKey]) {
          memosByClientId.set(clientId, formValues[memoKey] as string);
        }
      });

      // Create separate client and memo entries for each allocation
      clientsByAllocation.forEach((clientIds, allocationKey) => {
        adjustedValues[`clients_${allocationKey}`] = clientIds;

        clientIds.forEach(clientId => {
          const memo = memosByClientId.get(clientId);
          if (memo) {
            adjustedValues[`memo_${clientId}_${allocationKey}`] = memo;
          }
        });
      });

      // Update allocations with newly added clients
      newlyAddedClients.forEach((newClientIds, allocationKey) => {
        const [allocationUserId, targetAllocationId] = allocationKey.split('_').map(Number);
        // Use cached allocation lookup
        const allocation = allocationsById.get(targetAllocationId);
        if (allocation && allocation.user.id === allocationUserId) {
          const existingClientIds = new Set(allocation.user.clients?.map(c => c.id) || []);
          newClientIds.forEach(clientId => {
            // Check if client is not already in the allocation
            if (!existingClientIds.has(clientId)) {
              // Get memo from form values using pre-indexed memo fields
              const memoPrefix = `memo_${clientId}_${allocationUserId}_${targetAllocationId}`;
              const memoKey = Array.from(memoFieldsIndex.keys()).find(k =>
                k.startsWith(memoPrefix),
              );
              const memo = memoKey ? (formValues[memoKey] as string) : '';

              // Add minimal client object to the allocation
              allocation.user.clients.push({
                id: clientId,
                firstName: '',
                lastName: '',
                photo: '',
                dateOfBirth: '',
                medicaId: '',
                gender: { id: 0, name: '' },
                isArchived: false,
                memo: memo || '',
                coordinates: { lat: 0, lng: 0 },
              });
              existingClientIds.add(clientId); // Update Set for next iteration

              // Update the map to include this client
              if (!clientToAllocationMap.has(clientId)) {
                clientToAllocationMap.set(clientId, []);
              }
              clientToAllocationMap.get(clientId)?.push({
                userId: allocationUserId,
                allocationId: targetAllocationId,
              });
            }
          });
        }
      });
    } else if (key.startsWith('memo_')) {
      // Process memo fields that weren't handled in clients_* processing
      // This handles cases where memo was edited but clients array wasn't changed
      const parts = key.split('_');
      const userId = parts[2];
      const allocationId = parts[3];

      // If allocationId exists, this is a memo for a specific allocation
      if (allocationId) {
        // Check if this memo field should be included in adjustedValues
        // Find the corresponding clients_* field to see if this allocation was processed
        const clientsKey = `clients_${userId}_${allocationId}`;

        // If the clients field exists in formValues or adjustedValues, include this memo
        if (clientsKey in formValues || clientsKey in adjustedValues) {
          adjustedValues[key] = value;
        }
      } else {
        // User-level memo field (memo_CLIENTID_USERID) - copy as-is
        adjustedValues[key] = value;
      }
    } else if (key.startsWith('groups_')) {
      // Collect for second pass
      const parts = key.split('_');
      const userId = parts[1];
      groupsToProcess.push({ userId, value });
    } else {
      // Copy other fields as-is
      adjustedValues[key] = value;
    }
  });

  // Second pass: assign groups to split allocations
  // Pre-build a map of client keys to allocationIds for faster lookup
  const clientKeysToAllocationIds = new Map<string, string>();
  Object.keys(adjustedValues).forEach(clientKey => {
    if (clientKey.startsWith('clients_')) {
      const parts = clientKey.split('_');
      if (parts.length >= 3) {
        clientKeysToAllocationIds.set(clientKey, parts[2]);
      }
    }
  });

  groupsToProcess.forEach(({ userId, value }) => {
    clientKeysToAllocationIds.forEach((allocationId, clientKey) => {
      if (clientKey.startsWith(`clients_${userId}_`)) {
        adjustedValues[`groups_${userId}_${allocationId}`] = value;
      }
    });
  });

  // Third pass: remove clients from allocations that were removed from form values
  // Only modify allocations whose form fields were explicitly processed
  // Pre-build sets for faster lookups
  const adjustedValuesKeys = new Set(Object.keys(adjustedValues));
  const formValuesKeys = new Set(Object.keys(formValues));
  const userLevelFields = new Set(
    Object.keys(formValues).filter(k => k.startsWith('clients_') && k.split('_').length === 2),
  );

  updatedAllocations.forEach(allocation => {
    const userId = allocation.user.id;
    const allocationId = allocation.id;
    const formFieldKey = `clients_${userId}_${allocationId}`;
    const userLevelFieldKey = `clients_${userId}`;

    // If user-level field exists (without allocationId), leave allocation as-is
    if (userLevelFields.has(userLevelFieldKey)) {
      return;
    }

    // Only process allocations whose fields were explicitly in the form
    // If form field exists in adjustedValues, filter allocation clients to match form values
    if (adjustedValuesKeys.has(formFieldKey)) {
      // Get the clients that should remain from both adjustedValues and original formValues
      const adjustedFormClients = (adjustedValues[formFieldKey] as number[]) || [];
      const originalFormClients = (formValues[formFieldKey] as number[]) || [];

      // Use the adjusted form clients if available, otherwise use original
      // This ensures clients removed from form are also removed from allocation
      const clientsToKeep =
        adjustedFormClients.length > 0 ? adjustedFormClients : originalFormClients;
      const clientsToKeepSet = new Set(clientsToKeep);

      // Filter allocation to only keep clients that are in the form values
      allocation.user.clients = allocation.user.clients?.filter(client =>
        clientsToKeepSet.has(client.id),
      );
    }
    // If form field doesn't exist in adjustedValues, check if it was in original formValues
    else if (formValuesKeys.has(formFieldKey)) {
      // Field was in original form but not in adjustedValues (might have been split/merged)
      const originalFormClients = formValues[formFieldKey] as number[];

      // If original form had empty array, it means all clients were removed
      if (Array.isArray(originalFormClients) && originalFormClients.length === 0) {
        allocation.user.clients = [];
      }
      // Otherwise, the field was split/merged - check if any clients from this allocation
      // ended up in other adjustedValues fields
      else {
        // Find all form fields for this user that might contain clients from this allocation
        const userFormFields = Array.from(adjustedValuesKeys).filter(key =>
          key.startsWith(`clients_${userId}_`),
        );

        // Collect all client IDs from user's form fields
        const allUserFormClients = new Set<number>();
        userFormFields.forEach(key => {
          const fieldClients = (adjustedValues[key] as number[]) || [];
          fieldClients.forEach(clientId => allUserFormClients.add(clientId));
        });

        // If some clients from this allocation are in form fields, keep only those
        if (allUserFormClients.size > 0) {
          allocation.user.clients = allocation.user.clients?.filter(client =>
            allUserFormClients.has(client.id),
          );
        } else {
          // Field was in form but no clients ended up in adjustedValues - they were removed
          allocation.user.clients = [];
        }
      }
    }
    // If field doesn't exist in either adjustedValues or formValues,
    // check if there are any form fields for this user
    else {
      // Check if there are any form fields for this user in formValues
      const userFormFields = Array.from(formValuesKeys).filter(key =>
        key.startsWith(`clients_${userId}_`),
      );

      // If there are form fields for this user, we need to check if clients from this allocation
      // appear in any of those fields
      if (userFormFields.length > 0) {
        // Collect all client IDs that appear in any form field for this user
        const allUserFormClients = new Set<number>();
        userFormFields.forEach(key => {
          const fieldClients = (formValues[key] as number[]) || [];
          fieldClients.forEach(clientId => allUserFormClients.add(clientId));
        });

        // Filter allocation to only keep clients that appear in user's form fields
        allocation.user.clients = allocation.user.clients?.filter(client =>
          allUserFormClients.has(client.id),
        );
      }
      // If no form fields exist for this user, it wasn't edited - leave allocation as-is
    }
  });
  // Ensure all allocations from updatedAllocations are represented in adjustedValues
  // This ensures the structure matches updatedAllocations exactly
  updatedAllocations.forEach(allocation => {
    const allocationKey = `clients_${allocation.user.id}_${allocation.id}`;
    // If this allocation wasn't processed in the form, create an empty entry
    // or ensure it matches what's in updatedAllocations
    if (!(allocationKey in adjustedValues)) {
      // Get clients that belong to this allocation from updatedAllocations
      const allocationClients = allocation.user.clients?.map(c => c.id) || [];
      adjustedValues[allocationKey] = allocationClients;

      // Also add memos for these clients
      allocationClients.forEach(clientId => {
        const client = allocation.user.clients?.find(c => c.id === clientId);
        if (client?.memo) {
          adjustedValues[`memo_${clientId}_${allocation.user.id}_${allocation.id}`] = client.memo;
        }
      });
    }
  });

  return adjustedValues;
};
